import { Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import {
  NON_ROUTABLE_RESOLUTION_TYPE_LABEL,
  NON_ROUTABLE_STATUS_LABEL,
  RELAY_ERROR_LABEL,
  labelOf,
} from '../constants/nonRoutableRequests';
import formatRelayDate from '../utils/relayDates';

const SummaryField = ({ label, value, testId }) => (
  <Grid item xs={6} sm={4}>
    <Typography variant='caption' color='textSecondary' display='block'>
      {label}
    </Typography>
    <Typography
      variant='body2'
      sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
      data-testid={testId}
    >
      {value ?? 'N/A'}
    </Typography>
  </Grid>
);

SummaryField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  testId: PropTypes.string,
};

SummaryField.defaultProps = { value: undefined, testId: undefined };

const relayProblem = ({ relayErrorCode }) =>
  relayErrorCode ? `${labelOf(RELAY_ERROR_LABEL, relayErrorCode)} (${relayErrorCode})` : null;

/**
 * POM-6279 — the facts an operator decides Resend / Reconcile / Cancel on, for one parked relay
 * fax: where it was going, the fax service's handle and error, and whether a send is in flight.
 *
 * NOTHING HERE IS PHI and nothing may become so: ids, a pharmacy's business name and fax number,
 * the fax service's message id and its own error text (never PHI, by error_detail's contract).
 * The relay order's document storage keys are not served and are not shown.
 */
const NonRoutableRelayFaxSummary = ({ request, relay }) => (
  <Grid container spacing={1.5}>
    <SummaryField label='Reference Id' value={request.referenceId} />
    <SummaryField label='Destination pharmacy' value={relay.destinationName} />
    <SummaryField label='Fax number' value={relay.destinationEndpoint} />
    <SummaryField label='Fax problem' value={relayProblem(relay)} />
    <SummaryField label='Fax service detail' value={relay.relayErrorDetail} />
    <SummaryField label='Transmission reference' value={relay.transmissionReference} />
    {/* The one-send claim. While it is held a fax MAY be at the pharmacy, which is why a resend
        then needs the fax service's answer or a phone call, and a cancel tells them to phone. */}
    <SummaryField
      label='Send in progress'
      testId='relay-fax-claim-held'
      value={relay.isClaimHeld ? 'Yes — the pharmacy may already have this fax' : 'No'}
    />
    <SummaryField label='Fax attempts' value={relay.attemptCount} />
    <SummaryField label='Last attempt' value={formatRelayDate(relay.lastAttemptAt)} />
    <SummaryField label='Dead-lettered' value={formatRelayDate(relay.dlqAt)} />
    <SummaryField
      label='Request status'
      value={labelOf(NON_ROUTABLE_STATUS_LABEL, request.status)}
    />
    <SummaryField
      label='Resolution'
      value={
        request.resolutionType &&
        labelOf(NON_ROUTABLE_RESOLUTION_TYPE_LABEL, request.resolutionType)
      }
    />
  </Grid>
);

NonRoutableRelayFaxSummary.propTypes = {
  request: PropTypes.shape({
    referenceId: PropTypes.string,
    status: PropTypes.string,
    resolutionType: PropTypes.string,
  }).isRequired,
  relay: PropTypes.shape({
    destinationName: PropTypes.string,
    destinationEndpoint: PropTypes.string,
    transmissionReference: PropTypes.string,
    relayErrorCode: PropTypes.string,
    relayErrorDetail: PropTypes.string,
    isClaimHeld: PropTypes.bool,
    attemptCount: PropTypes.number,
    lastAttemptAt: PropTypes.string,
    dlqAt: PropTypes.string,
  }).isRequired,
};

export default NonRoutableRelayFaxSummary;
